import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { type InternalAxiosRequestConfig } from 'axios'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { resetAnalytics } from '../lib/analytics'
import { apiErrorDetail } from '../lib/apiError'
import { CacheKeys } from '../lib/cache'
import { getCsrfCookieToken } from '../lib/csrfCookie'

const AUTH_URL = '/api/v1/insure/auth/'
const TRY_AGAIN = "We couldn't sign you in just now. Please try again."
// Writing it fires a storage event in this browser's other tabs.
const ACCOUNT_CHANGED_KEY = 'insure-account-changed'

/** A 401 or 403 answers for the session itself, which no retry will change. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401 || status === 403) return false
  }
  return failureCount < 3
}

/**
 * Send this browser's PostHog ids with a request to our own API, so the funnel events the
 * backend captures land on the same person, and session replay, as the page views.
 */
export function withPosthogHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  // Our API only: a custom header on a cross-origin call (signed storage URLs) would need CORS approval.
  if (!posthog.__loaded || !config.url?.startsWith('/api/')) return config
  const distinctId = posthog.get_distinct_id()
  const sessionId = posthog.get_session_id()
  if (distinctId) config.headers['X-PostHog-Distinct-Id'] = distinctId
  if (sessionId) config.headers['X-PostHog-Session-Id'] = sessionId
  return config
}

export function setPosthogHeaders() {
  axios.interceptors.request.use(withPosthogHeaders)
}

//axios interceptor to add csrf token to every request
export function setCsrfHeader() {
  axios.interceptors.request.use((config) => {
    if (config.method !== 'get') {
      const token = getCsrfCookieToken()
      // Only set Content-Type to application/json if not sending FormData
      // FormData needs multipart/form-data with boundary, which axios sets automatically
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json'
      }
      config.headers['x-csrftoken'] = token
    }
    return config
  })
}

export interface AuthStatus {
  authenticated: boolean
  /** The signed-in Insure user, or null while signed out or still unknown. */
  userId: string | null
}

/** Whether this browser is signed in, and as whom. The response also sets the CSRF cookie every write needs. */
export function getAuthStatus(): Promise<AuthStatus> {
  return axios.get<{ authenticated: boolean; user_id?: string | null }>(AUTH_URL).then((response) => ({
    authenticated: response.data.authenticated,
    userId: response.data.user_id ?? null,
  }))
}

/** The browser's signed-in state. Load it before any write, so the CSRF cookie exists. */
export function useAuthStatus() {
  return useQuery({ queryKey: [CacheKeys.AUTH], queryFn: getAuthStatus })
}

/** Email a sign-in code. The answer is the same whether or not the address has an account. */
export function sendSignInCode(email: string): Promise<void> {
  return axios.post(`${AUTH_URL}code/`, { email }).then(() => undefined)
}

/** Sign in with an emailed code; an address with no account gets a new one. */
export function verifySignInCode(email: string, code: string): Promise<void> {
  return axios.post(AUTH_URL, { email, code }).then(() => undefined)
}

/** Sign in with a one-use ticket, replacing whichever session this browser had. */
export function redeemSignInTicket(ticket: string): Promise<void> {
  return axios.post(`${AUTH_URL}ticket/`, { ticket }).then(() => undefined)
}

export function logout(): Promise<void> {
  return axios.post(`${AUTH_URL}logout/`).then(() => undefined)
}

/** Where Google sign-in starts, as a full-page navigation; it comes back to nextPath. */
export function googleSignInHref(nextPath: string): string {
  return `${AUTH_URL}google/?next=${encodeURIComponent(nextPath)}`
}

/** What to tell someone whose code, or sign-in link, didn't sign them in. */
export function signInErrorMessage(error: unknown): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  if (status === 400 || status === 409) return apiErrorDetail(error, TRY_AGAIN)
  if (status === 429) return 'Too many attempts. Wait a minute and try again.'
  return TRY_AGAIN
}

/** For the query cache: a 401 means the session ended, so the sign-in gates check it again. */
export function recheckSessionOnUnauthorized(queryClient: QueryClient, error: unknown): void {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    void queryClient.invalidateQueries({ queryKey: [CacheKeys.AUTH] }, { cancelRefetch: false })
  }
}

/**
 * Point the cache at the account that just signed in. Nothing cached before belongs to
 * it: that was the signed-out browser's, or another account's.
 */
export function markSignedIn(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== CacheKeys.AUTH })
  // Signed in; which user arrives with the refetch.
  queryClient.setQueryData<AuthStatus>([CacheKeys.AUTH], { authenticated: true, userId: null })
  void queryClient.invalidateQueries({ queryKey: [CacheKeys.AUTH] })
  announceAccountChange()
}

/** Tell this browser's other tabs that it signed in or out. */
export function announceAccountChange(): void {
  try {
    localStorage.setItem(ACCOUNT_CHANGED_KEY, String(Date.now()))
  } catch {
    // Storage can be off; the other tabs still check again when they're next shown.
  }
}

/**
 * Reload when this browser's session now belongs to someone else, so no tab shows one account
 * while its requests act as another. Checks when another tab announces a change, and when this one is
 * shown or focused.
 */
export function useFollowAccountChanges(): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    async function recheck() {
      const shown = queryClient.getQueryData<AuthStatus>([CacheKeys.AUTH])
      if (!shown) return
      const current = await getAuthStatus().catch(() => null)
      if (!current) return
      const switched = shown.userId !== null && current.userId !== shown.userId
      if (current.authenticated !== shown.authenticated || switched) window.location.reload()
    }
    function onStorage(event: StorageEvent) {
      if (event.key === ACCOUNT_CHANGED_KEY) void recheck()
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') void recheck()
    }
    // Focus too: a second window stays visible, and Google's full-page return announces nothing.
    function onFocus() {
      void recheck()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [queryClient])
}

/** Sign out, drop the analytics identity and the cache, and reload on the home page. */
export function useSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      resetAnalytics()
      queryClient.clear()
      announceAccountChange()
      window.location.assign('/insure')
    },
    onError: () => toast.error("We couldn't sign you out. Please try again."),
  })
}
